import { useEffect } from "react";
import { collectionRef } from "@/api/collectionRef/collectionOperations";
import { useChallengeStore } from "@/store/challenges/challenges.store.v2";
import { Challenge } from "@/models/database/challenges/challenge.model";
import { getAuthorProfileBasedOnUid } from "@/api/search/searchUsernameByName";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";

export const useSingleTenantChallenges = (slug: string) => {
  const {
    challenges,
    loading,
    error,
    setChallenges,
    setLoading,
    setError,
    getChallengeBySlug,
  } = useChallengeStore();

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const challengesArray: Challenge[] = [];

      const ref = collectionRef.challenge
        .where("client", "==", brand.tenantId)
        .where("slug", "==", slug);
      const query = await ref.get();

      // Get challenges from Firestore
      await Promise.all(
        query.docs.map(async (doc) => {
          const author = await getAuthorProfileBasedOnUid(
            doc.data().createdBy,
            doc.data().client
          );
          const challengeData = { author: author, id: doc.id, ...doc.data() };
          challengesArray.push(challengeData as Challenge);
          return challengeData;
        })
      );

      const updatedChallenges = [...(challenges || []), ...challengesArray];
      // Remove duplicates based on challenge ID
      const uniqueChallenges = Array.from(
        new Map(
          updatedChallenges.map((challenge) => [challenge.slug, challenge])
        ).values()
      );

      setChallenges(uniqueChallenges);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Check if the challenge already exists in store

  useEffect(() => {
    // Fetch challenges only if the specific challenge doesn't exist in store
    const existingChallenge = getChallengeBySlug(slug);

    if (!existingChallenge) {
      fetchChallenges();
    }
  }, [slug]);

  // Get the single challenge object instead of an array
  const challenge =
    getChallengeBySlug(slug) ||
    challenges?.find((challenge) => challenge.slug === slug) ||
    null;

  return {
    data: challenge,
    loading,
    error,
  };
};
